package com.dotdrives.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonElement

@Serializable
data class EmployerMatch(
    val employer: String,
    val domain: String = "",
    val instructions: String = "",
    val url: String? = null,
)

@Serializable
data class ContributionPolicy(
    @SerialName("basis_points") val basisPoints: Int = 0,
    @SerialName("fixed_cents") val fixedCents: Int = 0,
    val enabled: Boolean = false,
    val label: String = "Optional contribution to support payment processing",
)

@Serializable
data class PublicGivingForm(
    val id: String,
    @SerialName("org_id") val orgId: String,
    @SerialName("public_slug") val publicSlug: String,
    @SerialName("org_name") val orgName: String,
    val name: String,
    val headline: String,
    val version: Int,
    val config: FormConfig,
    @SerialName("payments_ready") val paymentsReady: Boolean = false,
    val currency: String = "usd",
    @SerialName("close_at") val closeAt: String? = null,
    @SerialName("contribution_policy")
    val contributionPolicy: ContributionPolicy = ContributionPolicy(),
    val progress: PublicGivingProgress? = null,
)

@Serializable data class PublicGivingProgress(val goal: Double, val received: Double)

@Serializable
data class PublicCheckoutRequest(
    @SerialName("org_id") val orgId: String,
    @SerialName("public_slug") val publicSlug: String,
    @SerialName("operation_id") val operationId: String,
    @SerialName("form_version") val formVersion: Int,
    val amount: String,
    val frequency: String,
    @SerialName("donor_name") val donorName: String,
    @SerialName("donor_email") val donorEmail: String,
    val fields: Map<String, JsonElement> = emptyMap(),
    @SerialName("cover_fees") val coverFees: Boolean = false,
    val anonymous: Boolean = false,
    val action: String = "checkout",
)

@Serializable
data class PublicCheckoutResult(
    val url: String?,
    @SerialName("operation_id") val operationId: String,
    @SerialName("status_token") val statusToken: String,
    @SerialName("gift_cents") val giftCents: Int,
    @SerialName("contribution_cents") val contributionCents: Int,
    @SerialName("total_cents") val totalCents: Int,
    val currency: String,
    val status: String,
)

@Serializable
data class PublicPaymentStatus(
    val status: String,
    @SerialName("reconciliation_required") val reconciliationRequired: Boolean = false,
)

/** Session-only recovery, never a URL or analytics payload. */
@Serializable
data class PublicGivingAttempt(
    val request: PublicCheckoutRequest,
    val statusToken: String? = null,
    val contributionCents: Int = 0,
)

fun givingCents(text: String): Int? {
    val value = text.trim()
    if (!Regex("[0-9]{1,7}(\\.[0-9]{1,2})?").matches(value)) return null
    val major = value.substringBefore('.').toLongOrNull() ?: return null
    val minor = value.substringAfter('.', "").padEnd(2, '0').toIntOrNull() ?: return null
    return (major * 100 + minor).takeIf { it in 50..100000000 }?.toInt()
}

fun givingAmount(cents: Int): String = "${cents / 100}.${(cents % 100).toString().padStart(2, '0')}"

fun safeGivingUrl(url: String?): Boolean =
    url != null &&
        Regex("https://[A-Za-z0-9.-]+(?::443)?(?:[/?#][^\\s]*)?").matches(url) &&
        !url.contains('\\')

fun safeCheckoutUrl(url: String): Boolean =
    Regex("https://checkout\\.stripe\\.com/[^\\s\\\\]+").matches(url)

fun potentialEmployerMatch(
    email: String,
    employer: String,
    rules: List<EmployerMatch>,
): EmployerMatch? {
    val domain = email.trim().substringAfter('@', "").lowercase()
    return rules.firstOrNull {
        (it.domain.isNotBlank() && domain == it.domain.trim().lowercase()) ||
            (employer.isNotBlank() && employer.trim().equals(it.employer.trim(), ignoreCase = true))
    }
}

@Serializable
data class PublicRestartResult(
    @SerialName("operation_id") val operationId: String,
    @SerialName("restart_allowed") val restartAllowed: Boolean,
)

/** Reserved contact values must remain free-form strings, never choice/boolean answers. */
fun compatibleContactField(field: FormField): Boolean =
    when (field.key) {
        "email" -> field.type in listOf("email", "text") && field.options == null
        "name",
        "full_name",
        "first_name",
        "last_name" -> field.type == "text" && field.options == null
        else -> true
    }
